import React, { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import CityCharts from './CityCharts';


// Old:
// const DATA_URL = "/data/10_target_cities_full.json";
// const COUNTS_URL = "/data/11_town_size_counts.json";

// New (single source of truth):
const FINAL_URL = "/data/13_final_master_cities.json";
const FACILITIES_URL = "/data/15_facilities_by_350_patched.json";



function formatCurrency(n) {
  if (n == null || isNaN(n)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `$${Math.round(n)}`;
  }
}
function formatNumber(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat().format(n);
}
function formatSqftPerCap(n) {
  if (n == null || isNaN(n)) return "—";
  return n.toFixed(2);
}

// --- helpers local to exportPDF (so you don't have to add them elsewhere) ---
const PLACE_TYPE_RE = /\s+(city|town|village|borough|municipality|cdp)$/i;
const normalizeType = (t = "") => {
  const l = t.toLowerCase();
  return l === "cdp" ? "CDP" : l.charAt(0).toUpperCase() + l.slice(1);
};
const toTitleCase = (s = "") => {
  const small = new Set(["and","or","of","the","a","an","for","in","on","at","by","to","de","del","la","le","da","dos","das","du","van","von","di"]);
  return s
    .toLowerCase()
    .split(/([-\s])/)
    .map((w, i) => {
      if (/^[-\s]$/.test(w)) return w;
      if (w.toUpperCase() === "CDP") return "CDP";
      if (i !== 0 && small.has(w)) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join("");
};
const parseTownParts = (tn = "") => {
  const [left = "", rawState = ""] = tn.split(",").map((s) => s.trim());
  const m = left.match(PLACE_TYPE_RE);
  const base = m ? left.slice(0, m.index).trim() : left;
  const type = m ? normalizeType(m[1]) : "";
  return { city: toTitleCase(base), state: toTitleCase(rawState), type };
};


export default function MarketScoutReport() {
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [states, setStates] = useState([]);
  const [sortKey, setSortKey] = useState("sqft_per_capita");
  const [sortDir, setSortDir] = useState("asc");


  // Filters
  const [selectedStates, setSelectedStates] = useState([]);
  const [minPop, setMinPop] = useState(5000);
  const [maxPop, setMaxPop] = useState(25000);
  const [maxSqftPerCap, setMaxSqftPerCap] = useState(10);
  const [hideThin, setHideThin] = useState(true);
  const [minIncome, setMinIncome] = useState(60000);

// Size assumptions (sqft per facility)
    const [sizeSmall,  setSizeSmall]  = useState(25000);
    const [sizeMedium, setSizeMedium] = useState(40000);
    const [sizeLarge,  setSizeLarge]  = useState(55000);
    const DEFAULT_SIZES = Object.freeze({ small: 25000, medium: 40000, large: 55000 });

    // counts from 11_town_size_counts.json
    const [countsMap, setCountsMap] = useState(new Map()); // you already created this in your effect

    function handleSort(key) {
  if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  else { setSortKey(key); setSortDir("asc"); }
}

const [facilitiesMap, setFacilitiesMap] = useState(new Map());

useEffect(() => {
  let alive = true;
  fetch(FACILITIES_URL)
    .then((r) => r.json())
    .then((arr) => {
      if (!alive) return;
      // arr is [{ town_key, facilities: [...] }, ...]
      const map = new Map(
        Array.isArray(arr)
          ? arr.map((x) => [x.town_key, x.facilities || []])
          : []
      );
      setFacilitiesMap(map);
    })
    .catch((err) => console.error("Failed to load facilities:", err));
  return () => { alive = false; };
}, []);

// Draw income vs price scatter directly into jsPDF.
// Returns the vertical space consumed (so you can advance `y`).
function drawIncomeVsPriceScatter({
  doc,
  data,              // array of towns (use `raw` or `filtered`)
  selected,          // the row being exported
  x, y, width, height
}) {
  // Extract valid points
  const pts = (data || [])
    .filter(d => Number.isFinite(d?.median_income) && Number.isFinite(d?.mean_price))
    .map(d => ({
      inc: +d.median_income,
      price: +d.mean_price,
      key: d.town_key,
    }));

  if (pts.length === 0) {
    return 0;
  }

  // Inner padding for axes/labels
  const padL = 50, padR = 12, padT = 18, padB = 42;
  const innerW = Math.max(10, width  - padL - padR);
  const innerH = Math.max(10, height - padT - padB);

  // Extents + a little breathing room
  const incMin = Math.min(...pts.map(p => p.inc));
  const incMax = Math.max(...pts.map(p => p.inc));
  const prMin  = Math.min(...pts.map(p => p.price));
  const prMax  = Math.max(...pts.map(p => p.price));
  const incPad = (incMax - incMin) * 0.05 || 1;
  const prPad  = (prMax  - prMin)  * 0.05 || 1;

  const xScale = v =>
    x + padL + ((v - (incMin - incPad)) / ((incMax + incPad) - (incMin - incPad))) * innerW;
  const yScale = v =>
    y + padT + (1 - (v - (prMin - prPad)) / ((prMax + prPad) - (prMin - prPad))) * innerH;

  // Frame
  doc.setDrawColor(200);
  doc.setLineWidth(0.7);
  doc.rect(x, y, width, height);

  // Axes
  doc.setDrawColor(160);
  doc.line(x + padL, y + padT,           x + padL,           y + padT + innerH); // Y
  doc.line(x + padL, y + padT + innerH,  x + padL + innerW,  y + padT + innerH); // X

  // Axis labels (no canvas rotation)
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  // X label
  doc.text("Median Household Income", x + padL + innerW / 2, y + padT + innerH + 28, { align: "center" });
  // Y label (rotated text only)
  doc.text("Avg 10x10 Price", x + 16, y + padT + innerH / 2, { align: "center", angle: -90 });

  // Ticks
  const ticks = 4;
  const fmt = n => new Intl.NumberFormat().format(Math.round(n));

  for (let i = 0; i <= ticks; i++) {
    const v = incMin - incPad + i * ((incMax + incPad) - (incMin - incPad)) / ticks;
    const tx = xScale(v);
    doc.setDrawColor(220); doc.line(tx, y + padT + innerH, tx, y + padT + innerH + 4);
    doc.setTextColor(80,80,80); doc.text(fmt(v), tx, y + padT + innerH + 16, { align: "center" });
  }
  for (let i = 0; i <= ticks; i++) {
    const v = prMin - prPad + i * ((prMax + prPad) - (prMin - prPad)) / ticks;
    const ty = yScale(v);
    doc.setDrawColor(220); doc.line(x + padL - 4, ty, x + padL, ty);
    doc.setTextColor(80,80,80); doc.text(fmt(v), x + padL - 6, ty + 3, { align: "right" });
  }

  // Points
  doc.setDrawColor(180);
  pts.forEach(p => {
    const px = xScale(p.inc);
    const py = yScale(p.price);
    doc.circle(px, py, 2.2, "F");
  });

  // Highlight selected with a red X
  const selInc = +selected?.median_income;
  const selPrice = +selected?.mean_price;
  if (Number.isFinite(selInc) && Number.isFinite(selPrice)) {
    const sx = xScale(selInc);
    const sy = yScale(selPrice);
    const r = 5;
    doc.setDrawColor(200, 0, 0);
    doc.setLineWidth(1.2);
    doc.line(sx - r, sy - r, sx + r, sy + r);
    doc.line(sx - r, sy + r, sx + r, sy - r);
  }

  // Small title inside the chart area
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text("Income vs Avg 10×10 (red = selected city)", x + padL, y + 12);

  // Return how much vertical space this block used
  return height;
}


// Export Facility PDF Section Begin

function exportFacilitiesPDF(row) {
  // ---- helpers ----
  const prettyUrl = (u = "") => {
    try { const url = new URL(u); return url.host.replace(/^www\./, "") + (url.pathname === "/" ? "" : url.pathname); }
    catch { return (u || "").replace(/^https?:\/\//, ""); }
  };
  const wrap = (doc, text, maxWidth) => doc.splitTextToSize(text || "—", maxWidth);
  const priceSourceLabel = row.price_source === "C" ? "City" : row.price_source === "S" ? "State" : "—";
  const facs = facilitiesMap.get(row.town_key) || [];

  // --- PDF setup ---
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
  const margin = 40;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - margin * 2;

  // Rhythm
  const lineHeight = 14;
  const gapSm = 6;
  const gapMd = 10;
  const gapLg = 16;

  // --- header bar (light blue) ---
  const { city, state, type } = parseTownParts(row.town_name || "");
  const title = `${city}, ${state}`;

  // light blue bar
  doc.setFillColor(150, 233, 255); // light blue
  doc.rect(0, 0, pageW, 64, "F");

  // header text (navy-ish)
  doc.setTextColor(15, 45, 90);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("House Properties MarketScout", margin, 30);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(title, margin, 52);

  // subheader (right-aligned)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(35, 70, 120);
  const generatedOn = new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const sub = `${type || "—"} · Facilities within ~6.2 miles · Generated ${generatedOn}`;
  doc.text(sub, pageW - margin - doc.getTextWidth(sub), 30);

  // reset to body defaults
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);

  let y = 64 + gapLg; // start comfortably below header

  // --- KPI tiles: fixed 3 columns x 2 rows ---
  const tiles = [
    { label: "Population", value: row.population?.toLocaleString?.() ?? "—" },
    { label: "Median Income", value: row.median_income?.toLocaleString?.() ?? "—" },
    { label: "Facilities", value: row.num_facilities ?? "—" },
    { label: "Est. Rentable SqFt", value: row.total_rentable_sqft_est?.toLocaleString?.() ?? "—" },
    { label: `Avg NCC 10x10 (${priceSourceLabel})`, value: row.mean_price ? `$${Math.round(row.mean_price)}` : "—" },
    { label: "SqFt per Capita", value: (row._est_sqft_per_capita ?? row.sqft_per_capita ?? null) ? (row._est_sqft_per_capita ?? row.sqft_per_capita).toFixed(2) : "—" },
  ];

  const cols = 3;
  const rows = 2; // fixed 6 tiles => 2 rows
  const gutter = 14;
  const tileW = (contentW - (cols - 1) * gutter) / cols;
  const tileH = 60;

  const drawTile = (x, y0, t) => {
    // thicker border + accent bar to "pop"
    doc.setDrawColor(160, 190, 230);
    doc.setLineWidth(1.2);
    doc.roundedRect(x, y0, tileW, tileH, 7, 7);
    // accent bar at top
    doc.setFillColor(200, 220, 255);
    doc.roundedRect(x, y0, tileW, 16, 7, 7, "F");

    // label
    doc.setTextColor(45, 70, 120);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(t.label, x + 10, y0 + 12);

    // value
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(String(t.value), x + 10, y0 + 36);
  };

  let tileX = margin, tileY = y;
  let tileIndex = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const t = tiles[tileIndex++];
      drawTile(tileX, tileY, t);
      tileX += tileW + gutter;
    }
    tileX = margin;
    tileY += tileH + gutter;
  }
  // move y safely below tiles (prevents overlap with "Facilities")
  y = tileY - gutter + gapLg;

  // === CHARTS: Supply vs Target + Nearby comps (sqft/cap & price) ===
(() => {
  // layout + colors
  const chartGap = 12, cardPad = 10;
  const gaugeH = 110, barRowH = 18;
  const titleRGB = [45,70,120], txtRGB = [0,0,0];
  const panelRGB = [21,26,46], strokeRGB = [42,49,82], accentRGB = [30,123,255], dimRGB = [200,213,237];

  const text = (s, xx, yy, size=10, rgb=txtRGB, bold=false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...rgb);
    doc.text(String(s), xx, yy);
  };

  // 1) Supply vs Target (progress gauge)
  const target = 10;           // sqft/person target (tweak)
  const facilitySize = 40000;  // per-facility sqft (tweak)
  const curr = Number(row.sqft_per_capita) || 0;
  const pop = Number(row.population) || 0;
  const totalSqft = Number(row.total_rentable_sqft_est) || 0;
  const gapSqft = Math.max(0, target * pop - totalSqft);
  const needed = Math.ceil(gapSqft / facilitySize);
  const pct = target > 0 ? Math.max(0, Math.min(1, curr / target)) : 0;

  // card box
  doc.setDrawColor(160,190,230); doc.setLineWidth(1);
  doc.roundedRect(margin, y, contentW, gaugeH, 7, 7);
  text("Supply vs Target", margin + cardPad, y + 18, 11, titleRGB, true);

  // gauge bg
  const gx = margin + cardPad, gw = contentW - cardPad*2, gh = 16, gy = y + 36;
  doc.setFillColor(...panelRGB); doc.setDrawColor(...strokeRGB);
  doc.roundedRect(gx, gy, gw, gh, 8, 8, "FD");

  // gauge fill
  doc.setFillColor(...accentRGB);
  doc.roundedRect(gx, gy, gw * pct, gh, 8, 8, "F");

  // target tick
  doc.setDrawColor(...dimRGB); doc.setLineWidth(0.8);
  doc.line(gx + gw - 1, gy - 6, gx + gw - 1, gy + gh + 6);

// tighter labels
const baseY = gy + gh + 20; // was +26
text(`Target: ${target} sqft/person`, gx, baseY, 10, [80,80,80]);
text(`Gap: ${gapSqft.toLocaleString()} sqft`, gx + 180, baseY, 10, [80,80,80]);      // was +240
text(`Needed facilities: ${isFinite(needed)?needed:0} (~${facilitySize.toLocaleString()} sqft ea)`,
     gx + 340, baseY, 10, [80,80,80]);                                               // was +430


  y += gaugeH + chartGap;

  // 2+3) Nearby comps (use `raw` if available; else draw only the subject row)
  const toRad = d => d * Math.PI / 180;
  const distKm = (a,b) => {
    const R=6371, dLat=toRad((b.lat||0)-(a.lat||0)), dLon=toRad((b.lon||0)-(a.lon||0));
    const la1=toRad(a.lat||0), la2=toRad(b.lat||0);
    const h = Math.sin(dLat/2)**2 + Math.cos(la1)*Math.cos(la2)*Math.sin(dLon/2)**2;
    return 2*R*Math.asin(Math.min(1,Math.sqrt(h)));
  };

  const here = { lat: row.lat, lon: row.lon, town_key: row.town_key };
  const pool = (typeof raw !== "undefined" && Array.isArray(raw) ? raw : [])
    .filter(t => t && t.town_key !== row.town_key && isFinite(t.lat) && isFinite(t.lon));
  const nearest = pool.map(t => ({...t, _km: distKm(here, t)})).sort((a,b)=>a._km-b._km).slice(0,5);

  const cityName = (row.abbreviated_city || (row.town_name ? row.town_name.split(",")[0] : "—")) + (row.state ? `, ${row.state}` : "");

  const drawBarList = (title, items, valueKey, suffix="") => {
    const rows = Math.max(1, items.length + 1); // +1 for subject city
    const cardH = 24 + rows*barRowH + 14;
    doc.setDrawColor(160,190,230);
    doc.roundedRect(margin, y, contentW, cardH, 7, 7);
    text(title, margin + cardPad, y + 18, 11, titleRGB, true);

    const max = Math.max(Number(row[valueKey])||0, ...items.map(n => Number(n[valueKey])||0));
    let yy = y + 30;
    const lblW = 170, valW = 90, barX = margin + cardPad + lblW, barW = contentW - lblW - valW - cardPad*2;

    const drawOne = (label, v, highlight=false) => {
      text(label, margin + cardPad, yy + 10, 10, highlight ? txtRGB : [80,80,80], highlight);
      // bg
      doc.setFillColor(...panelRGB); doc.setDrawColor(...strokeRGB);
      doc.roundedRect(barX, yy, barW, 12, 6, 6, "FD");
      // fill
      const w = max>0 ? (barW * (Number(v)||0) / max) : 0;
      const fill = highlight ? accentRGB : dimRGB;
      doc.setFillColor(...fill);
      doc.roundedRect(barX, yy, w, 12, 6, 6, "F");
      // value
      text(`${(Number(v)||0).toFixed(2)}${suffix}`, barX + barW + 8, yy + 10, 10, highlight ? txtRGB : [60,60,60], highlight);
      yy += barRowH;
    };

    drawOne(`${cityName} (you)`, row[valueKey], true);
    items.forEach(n => {
      const label = `${n.abbreviated_city ?? (n.town_name ? n.town_name.split(",")[0] : "—")}, ${n.state ?? ""}`;
      drawOne(label, n[valueKey], false);
    });

    y += cardH + chartGap;
  };

  // 2) sqft per capita (lowest first)
  const bySqft = [...nearest].filter(n => isFinite(n.sqft_per_capita)).sort((a,b)=>a.sqft_per_capita-b.sqft_per_capita);
  drawBarList("Nearby — Sqft per capita", bySqft, "sqft_per_capita");

  // 3) avg price (highest first)
  const byPrice = [...nearest].filter(n => isFinite(n.mean_price)).sort((a,b)=>(b.mean_price||0)-(a.mean_price||0));
  drawBarList("Nearby — Avg 10×10 Price", byPrice, "mean_price");
})();


 // Start "Facilities" on its own page — avoid blank page
if (y > margin + 5) {
  doc.addPage();
}
y = margin; // or margin + gapLg

doc.setFont("helvetica", "bold");
doc.setFontSize(12);
doc.setTextColor(15, 45, 90);
doc.text("Facilities", margin, y);
y += gapLg;

  // section divider + heading
  doc.setDrawColor(200, 210, 230);
  doc.setLineWidth(0.8);
  doc.line(margin, y, pageW - margin, y);
  y += gapLg;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);

  const addPageIfNeeded = (needed = 0) => {
    if (y + needed > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // Facility card renderer
  const renderFacility = (f, index) => {
    // Pre-calc for page break
    const addr = (f.address && f.address.trim()) || "";
    const nameLines = wrap(doc, f.name || "—", contentW - 30);
    const addrLines = addr ? wrap(doc, addr, contentW - 100) : ["—"];
    const webText = f.website ? prettyUrl(f.website) : "";
    const webLines = f.website ? wrap(doc, webText, contentW - 100) : [];

    const blockH =
      nameLines.length * lineHeight + gapSm +
      addrLines.length * lineHeight + gapMd +
      lineHeight + gapMd + // phone
      (f.website ? webLines.length * lineHeight + gapLg : gapLg) +
      2;

    addPageIfNeeded(blockH);

    // Index pill
    doc.setFillColor(32, 32, 32);
    doc.setTextColor(255, 255, 255);
    doc.roundedRect(margin, y - 11, 22, 22, 5, 5, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text(String(index + 1), margin + 11, y + 5, { align: "center", baseline: "middle" });

    // Name
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    const nameStartX = margin + 30;
    nameLines.forEach((ln, i2) => doc.text(ln, nameStartX, y + i2 * lineHeight));
    y += nameLines.length * lineHeight + gapSm;

    // Address (label + wrapped value, clickable)
    doc.setFont("helvetica", "normal"); doc.setFontSize(11);
    const addrLabel = "Address:";
    const addrLabelW = doc.getTextWidth(addrLabel) + 4;
    const addrValueX = nameStartX + addrLabelW;

    doc.text(addrLabel, nameStartX, y);
    if (addr) {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
      doc.setTextColor(0, 102, 204);
      addrLines.forEach((ln, i2) => doc.textWithLink(ln, addrValueX, y + i2 * lineHeight, { url: mapsUrl }));
      doc.setTextColor(0, 0, 0);
    } else {
      doc.text("—", addrValueX, y);
    }
    y += addrLines.length * lineHeight + gapMd;

    // Phone
    const phoneLabel = "Phone:";
    const phoneLabelW = doc.getTextWidth(phoneLabel) + 4;
    const phoneValueX = nameStartX + phoneLabelW;
    doc.text(phoneLabel, nameStartX, y);
    doc.text(f.phone?.trim() || "—", phoneValueX, y);
    y += lineHeight + gapMd;

    // Website (only if present)
    if (f.website) {
      const webLabel = "Website:";
      const webLabelW = doc.getTextWidth(webLabel) + 4;
      const webValueX = nameStartX + webLabelW;
      doc.text(webLabel, nameStartX, y);

      doc.setTextColor(0, 102, 204);
      webLines.forEach((ln, i2) => doc.textWithLink(ln, webValueX, y + i2 * lineHeight, { url: f.website }));
      doc.setTextColor(0, 0, 0);
      y += webLines.length * lineHeight + gapLg;
    } else {
      y += gapLg;
    }

    // Divider for card
    doc.setDrawColor(225, 230, 240);
    doc.setLineWidth(0.8);
    doc.line(margin, y, pageW - margin, y);
    y += gapLg;
  };

  if (facs.length === 0) {
    doc.text("No facilities found for this town.", margin, y);
  } else {
    facs.forEach((f, i) => renderFacility(f, i));
  }

// === Data & Methodology — Sources, Assumptions & Limitations ===
doc.addPage();
y = margin;

doc.setFont("helvetica", "bold");
doc.setFontSize(12);
doc.setTextColor(15, 45, 90);
doc.text("Data & Methodology — Sources, Assumptions & Limitations", margin, y);
y += 10;

doc.setDrawColor(200, 210, 230);
doc.setLineWidth(0.8);
doc.line(margin, y, pageW - margin, y);
y += 16;

doc.setFont("helvetica", "normal");
doc.setFontSize(9);
doc.setTextColor(60, 60, 60);

// Intro & liability
[
  "This report is for informational purposes only and does not constitute investment advice or a guarantee of performance. Figures are estimates compiled from public sources, APIs, and internal methods and may contain errors or omissions. Market conditions and pricing change frequently.",
  "Professional AI Agents LLC and House Properties make no representation or warranty, express or implied, as to the accuracy or completeness of the information herein and accept no liability for decisions made using this report."
].forEach(p => {
  wrap(doc, p, contentW).forEach(ln => { doc.text(ln, margin, y); y += 12; });
  y += 6;
});

// Section title
doc.setFont("helvetica", "bold");
doc.setTextColor(15, 45, 90);
doc.text("Sources & Method Notes", margin, y);
y += 12;

doc.setFont("helvetica", "normal");
doc.setTextColor(60, 60, 60);

// Bullet list
[
  "Population & median income: U.S. Census Bureau (ACS 5-year estimates).",
  "Places (lat/long): Nominatim; e.g., GeoNames/USGS GNIS).",
  "Universe of places: 3,206 U.S. cities, towns, villages, boroughs and CDPs.",
  "Facility universe: Google Places API returned ~94,000 self-storage POIs near those 3,206 places. Professional AI Agents LLC then manually filtered false positives (e.g., supermarkets, auto parts), resulting in ~90,000 actual self-storage facilities.",
  "Supply per capita: estimated rentable square feet = facility count × 40,000 (assumed typical facility size). Sqft/person = estimated sqft ÷ population.",
  "Market selection (House requested): California, Idaho, Montana, Washington, Oregon, Arizona, Utah, New Mexico, Wyoming, Colorado. Professional AI Agents LLC selected ~300 lowest-supply (sqft/person) places within these 10 states.",
  "Nationwide additions: To avoid missing standout markets outside the focus states, we added 50 lowest-supply places from the remaining 40 states.",
  "Rent data: Veritec Solutions provided average non-climate, ground-level 10×10 rates for 127 cities. For the remaining 223 cities, the state-level average rate was used.",
  "Final dataset comprises 350 cities encompassing 3,784 facilities as of September 4, 2025. This report is a single-point-in-time snapshot; several key metrics incorporate observations dating back to 2022 and are not continuously updated. It is intended to provide directional market context for identifying potential self-storage opportunities, not to serve as a live database of record."
].forEach(b => {
  wrap(doc, "• " + b, contentW).forEach(ln => { doc.text(ln, margin, y); y += 12; });
});

// Method caveats
y += 6;
[
  "Assumptions materially affect results. If average facility size differs from 40,000 sqft, or if competitor facility counts/areas are misclassified, supply per capita and “needed facilities” will change. Rows marked as thin data indicate incomplete or inconsistent observations.",
  "Effective rent ($/sf) is estimated as the average advertised 10×10 monthly rate divided by 100. Some markets may have atypical unit mixes, discounts, or fees not reflected in posted rates."
].forEach(p => {
  wrap(doc, p, contentW).forEach(ln => { doc.text(ln, margin, y); y += 12; });
});

// --- Ownership & License (Option B) ---
y += 10;

doc.setDrawColor(200, 210, 230);
doc.setLineWidth(0.8);
doc.line(margin, y, pageW - margin, y);
y += 14;

doc.setFont("helvetica", "normal");
doc.setFontSize(9);
doc.setTextColor(60, 60, 60);

const ownership =
"Ownership & License. This website and report (including data compilations, analyses, and visualizations) are the proprietary work of Professional AI Agents LLC. They are furnished to House Properties under a license for internal decision support. The materials may not be reproduced, redistributed, publicly posted, or used in any commercial offering, and the license may not be transferred or sublicensed, without prior written consent. Limited sharing with prospective partners or investors under confidentiality is permitted. Third-party data remains the property of its respective owners.";

wrap(doc, ownership, contentW).forEach(ln => { doc.text(ln, margin, y); y += 12; });



// === Footer (divider + 3 zones + ©) ===
const pages = doc.getNumberOfPages();
const stampDate =
  (typeof generatedOn !== "undefined" && generatedOn) ||
  new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

for (let p = 1; p <= pages; p++) {
  doc.setPage(p);
  const yFoot = pageH - 18;

  // subtle divider
  doc.setDrawColor(220, 225, 235);
  doc.setLineWidth(0.6);
  doc.line(margin, yFoot - 8, pageW - margin, yFoot - 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(110, 115, 125);

  // left: page count
  doc.text(`Page ${p} of ${pages}`, margin, yFoot);

  // center: confidentiality + timestamp
  doc.text(`Confidential • Generated ${stampDate}`, pageW / 2, yFoot, { align: "center" });

  // right: brand + ©
  const year = new Date().getFullYear();
  doc.text(`© ${year} Professional AI Agents LLC`, pageW - margin, yFoot, { align: "right" });
}

const safeCity = (city || row.abbreviated_city || "city").replace(/[^\w\-]+/g, "_");
doc.save(`${safeCity}_${row.state}_facilities.pdf`);
} // <--- end exportFacilitiesPDF


// Export PDF Section End

// Pulls the right value for each column (uses computed when available)
function getValue(row, key) {
  switch (key) {
    case "abbreviated_city":
      // sort by parsed city name for consistency with your display
      return (row.town_name && parseTownParts(row.town_name).city) || row.abbreviated_city || "";
    case "state":
      return (row.town_name && parseTownParts(row.town_name).state) || row.state || "";
    case "population":
      return row.population ?? 0;
    case "median_income":
      return row.median_income ?? 0;
    case "sqft_per_capita":
      return row._est_sqft_per_capita ?? row.sqft_per_capita ?? 0;
    case "mean_price":
      return row.mean_price ?? 0;
    case "num_facilities":
      return row.num_facilities ?? 0;
    case "total_rentable_sqft_est":
      return row._est_total_sqft ?? row.total_rentable_sqft_est ?? 0;
    case "type":
      return (row.town_name && parseTownParts(row.town_name).type) || "";
    default:
      return 0;
  }
}

// Safe compare for numbers or strings
function compareValues(a, b) {
  const an = typeof a === "number" && !Number.isNaN(a);
  const bn = typeof b === "number" && !Number.isNaN(b);
  if (an && bn) return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
}


useEffect(() => {
  let alive = true;
  setLoading(true);

  fetch(FINAL_URL)
    .then((r) => r.json())
    .then((towns) => {
      if (!alive) return;

      const arr = Array.isArray(towns) ? towns : [];
      setRaw(arr);

      // Build state list from cleaned names (parseTownParts)
      const allStates = Array.from(
        new Set(
          arr
            .map((d) => parseTownParts(d.town_name || "").state)
            .filter(Boolean)
        )
      ).sort();
      setStates(allStates);
      setSelectedStates(allStates);

      // Build countsMap directly from FINAL_URL (it already has small/medium/large counts)
      const map = new Map(arr.map((c) => [c.town_key, c]));
      setCountsMap(map);
    })
    .catch((err) => {
      // Optional: capture/diagnose in UI
      console.error("Failed to load FINAL_URL:", err);
    })
    .finally(() => {
      if (alive) setLoading(false);
    });

  return () => {
    alive = false;
  };
}, []);



const filtered = useMemo(() => {
  const minP = Number(minPop) || 0;
  const maxP = maxPop === "" || maxPop == null ? Infinity : Number(maxPop);
  const maxS = maxSqftPerCap === "" || maxSqftPerCap == null ? Infinity : Number(maxSqftPerCap);
  const qLower = q.trim().toLowerCase();
  const hasCounts = countsMap && countsMap.size > 0;

  // 1) enrich
  const enriched = raw.map((r) => {
    if (!hasCounts) return { ...r, _est_total_sqft: null, _est_sqft_per_capita: null };

    const c = countsMap.get(r.town_key) || {};
    const sc = (c.small_count  ?? r.small_count  ?? 0);
    const mc = (c.medium_count ?? r.medium_count ?? 0);
    const lc = (c.large_count  ?? r.large_count  ?? 0);

    const est_total_sqft = sc * sizeSmall + mc * sizeMedium + lc * sizeLarge;
    const est_sqft_per_capita = r.population ? est_total_sqft / r.population : null;

    return { ...r, _est_total_sqft: est_total_sqft, _est_sqft_per_capita: est_sqft_per_capita };
  });

  // 2) filter
  const filteredRows = enriched.filter((r) => {
    const pop = Number(r.population) || 0;
    const { city, state } = parseTownParts(r.town_name || "");
    const s = r._est_sqft_per_capita ?? r.sqft_per_capita ?? Infinity;

      const income = Number(r.median_income) || 0;   // ⬅ add this
      if (income < minIncome) return false;          // ⬅ and this

    // hide 0 & 1 facility towns
    if (hideThin && (r.num_facilities ?? 0) <= 1) return false;
    if (selectedStates.length && !selectedStates.includes(state)) return false;
    if (pop < minP || pop > maxP) return false;
    if (s > maxS) return false;

    if (qLower) {
      const hay = `${city} ${state} ${r.town_name || ""} ${r.town_key}`.toLowerCase();
      if (!hay.includes(qLower)) return false;
    }
    return true;
  });

  // 3) sort
  filteredRows.sort((a, b) => {
    const av = getValue(a, sortKey);
    const bv = getValue(b, sortKey);
    const aNull = av === null || av === undefined || av === "";
    const bNull = bv === null || bv === undefined || bv === "";
    if (aNull && bNull) return 0;
    if (aNull) return 1;   // nulls to bottom
    if (bNull) return -1;

    const an = typeof av === "number" && !Number.isNaN(av);
    const bn = typeof bv === "number" && !Number.isNaN(bv);
    const base = (an && bn) ? (av - bv)
      : String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
    return sortDir === "asc" ? base : -base;
  });

  return filteredRows;
}, [
  raw, countsMap,
  sizeSmall, sizeMedium, sizeLarge,
  hideThin, selectedStates,
  minPop, maxPop, maxSqftPerCap, q,
  // 👇 include these so sorting reruns when you click headers
  sortKey, sortDir,
  minIncome, 
]);




  function toggleState(s) {
    setSelectedStates((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  }
  function selectAllStates() {
    setSelectedStates(states);
  }
  function clearStates() {
    setSelectedStates([]);
  }
 function resetFilters() {
  setQ("");
  setMinPop(5000);
  setMaxPop(25000);
  setMaxSqftPerCap(10);
  setHideThin(true);
  setSelectedStates(states);
  setSizeSmall(DEFAULT_SIZES.small);
  setSizeMedium(DEFAULT_SIZES.medium);
  setSizeLarge(DEFAULT_SIZES.large);
  setMinIncome(60000);       // ⬅ add this
}

function exportPDF() {
  // ——— Setup ———
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
  const margin   = 40;
  const pageW    = doc.internal.pageSize.getWidth();
  const pageH    = doc.internal.pageSize.getHeight();
  const contentW = pageW - margin * 2;
  const wrap = (doc, text, maxWidth) => doc.splitTextToSize(text || "-", maxWidth);

  // Palette (matches city PDFs)
  const COLORS = {
    brandBar: [150, 233, 255],
    brandText: [15, 45, 90],
    stroke: [160, 190, 230],
    subText: [60, 60, 60],
    tableHeadBg: [21, 26, 46],
    tableAltRow: [245, 248, 255],
    footStroke: [220, 225, 235],
    footText: [110, 115, 125],
  };

  const generatedOn = new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

  // ——— Header (no overlap) ———
  const BRAND_H = 78;
  doc.setFillColor(...COLORS.brandBar);
  doc.rect(0, 0, pageW, BRAND_H, "F");

  // Title (auto-shrink so it never collides with the stamp)
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.brandText);
  let title = "House Properties MarketScout — Underserved Towns Report";
  let tSize = 16; doc.setFontSize(tSize);
  while (doc.getTextWidth(title) > (contentW - 140) && tSize > 12) { tSize -= 0.5; doc.setFontSize(tSize); }
  doc.text(title, margin, 34);

  // Generated stamp (right, lower in the bar)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(35, 70, 120);
  doc.text(`Generated ${generatedOn}`, pageW - margin, 52, { align: "right" });

  // Start content just below the brand bar
  let y = BRAND_H + 16;

  // ——— Filters panel (rounded box, clean spacing) ———
  const panelTop = y, panelPad = 10, lineH = 12;
// Panel contents
doc.setFont("helvetica", "bold");
doc.setFontSize(11);
doc.setTextColor(...COLORS.brandText);
let fY = panelTop + panelPad + 12;

// Summary
doc.text("Summary", margin + panelPad, fY);
doc.setFont("helvetica", "normal");
doc.setFontSize(10);
doc.setTextColor(...COLORS.subText);

const summary1 = "Dataset: 350 lowest sqft/cap towns; 300 from target 10 western states + 50 from the rest of US (out of ~3,206 towns/CDPs). Facilities counted within ~6.2 miles of town center.";

wrap(doc, summary1, contentW - panelPad * 2).forEach(line => { fY += lineH; doc.text(line, margin + panelPad, fY); });

fY += 16; // spacing before Filters

// Filters
doc.setFont("helvetica", "bold");
doc.setTextColor(...COLORS.brandText);
doc.text("Filters", margin + panelPad, fY);

doc.setFont("helvetica", "normal");
doc.setFontSize(10);
doc.setTextColor(...COLORS.subText);

// ASCII-only to avoid spacing glitches
const filt1 = `States Selected: ${selectedStates.length} • Population: ${formatNumber(minPop)}-${formatNumber(maxPop)} • Max SqFt/Cap: ${maxSqftPerCap} • Skip 0&1-Facility Towns: ${hideThin}`;
const filt2 = `Assumed Facility Sizes — Small: ${formatNumber(sizeSmall)} • Medium: ${formatNumber(sizeMedium)} • Large: ${formatNumber(sizeLarge)}`;

wrap(doc, filt1, contentW - panelPad * 2).forEach(line => { fY += lineH; doc.text(line, margin + panelPad, fY); });
wrap(doc, filt2, contentW - panelPad * 2).forEach(line => { fY += lineH; doc.text(line, margin + panelPad, fY); });

  
  const panelH = (fY - panelTop) + panelPad + 6;
  doc.setDrawColor(...COLORS.stroke); doc.setLineWidth(0.8);
  doc.roundedRect(margin, panelTop, contentW, panelH, 6, 6);

  y = panelTop + panelH + 12; // handoff to table

  // ——— Table rows ———
  const rows = filtered.map((r) => {
    const { city, state, type } = parseTownParts(r.town_name || "");
    const sqftPerCap = r._est_sqft_per_capita ?? r.sqft_per_capita;
    const totalSqft  = r._est_total_sqft ?? r.total_rentable_sqft_est;
    const priceTxt   = r.mean_price != null ? `${formatCurrency(r.mean_price)} ${r.price_source || ""}` : "—";
    return [
      city,
      type || "—",
      state,
      formatNumber(r.population),
      formatNumber(r.median_income),
      formatSqftPerCap(sqftPerCap),
      priceTxt,
      formatNumber(r.num_facilities),
      formatNumber(totalSqft),
    ];
  });

  // ——— Table (columns sum < contentW to respect margins) ———
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [[
      "Town", "Type", "State", "Population", "Median\nIncome",
      "SqFt\nPer Cap", "Avg\n10x10", "Facilities\n~6.2m", "Est. SqFt\nRentable",
    ]],
    body: rows,
    theme: "grid",
    tableWidth: contentW,
    showHead: "everyPage",
    headStyles: {
      fillColor: COLORS.tableHeadBg,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
      halign: "center",
      lineColor: COLORS.stroke,
      lineWidth: 0.6,
    },
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 4,
      overflow: "ellipsize",
      lineWidth: 0.4,
      lineColor: COLORS.stroke,
      textColor: [0,0,0],
      valign: "top",
    },
    alternateRowStyles: { fillColor: COLORS.tableAltRow },
    // Sum ≈ 506pt (< 532pt contentW)
    columnStyles: {
      0: { cellWidth: 116 },                 // Town
      1: { cellWidth: 36,  halign: "center" }, // Type
      2: { cellWidth: 54 },                  // State
      3: { cellWidth: 60,  halign: "right" }, // Population
      4: { cellWidth: 54,  halign: "right" }, // Median Inc.
      5: { cellWidth: 50,  halign: "right" }, // Sqft/Cap
      6: { cellWidth: 40,  halign: "right" }, // Avg 10x10
      7: { cellWidth: 48,  halign: "right" }, // Facilities
      8: { cellWidth: contentW - (116+36+54+60+54+50+40+48), halign: "right" },
    },
  });

  // ——— Disclaimer page (exact, with 3,784 facilities) ———
  doc.addPage();
  y = margin;

  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...COLORS.brandText);
  doc.text("Data & Methodology — Sources, Assumptions & Limitations", margin, y); y += 10;

  doc.setDrawColor(...COLORS.stroke); doc.setLineWidth(0.8);
  doc.line(margin, y, pageW - margin, y); y += 16;

  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...COLORS.subText);

  [
    "This report is for informational purposes only and does not constitute investment advice or a guarantee of performance. Figures are estimates compiled from public sources, APIs, and internal methods and may contain errors or omissions. Market conditions and pricing change frequently.",
    "Professional AI Agents LLC and House Properties make no representation or warranty, express or implied, as to the accuracy or completeness of the information herein and accept no liability for decisions made using this report."
  ].forEach(p => { wrap(doc, p, contentW).forEach(ln => { doc.text(ln, margin, y); y += 12; }); y += 6; });

  doc.setFont("helvetica", "bold"); doc.setTextColor(...COLORS.brandText);
  doc.text("Sources & Method Notes", margin, y); y += 12;

  doc.setFont("helvetica", "normal"); doc.setTextColor(...COLORS.subText);
  [
    "Population & median income: U.S. Census Bureau (ACS 5-year estimates).",
    "Places (lat/long): open-source gazetteers/geocoders (e.g., OSM Nominatim, GeoNames, USGS GNIS).",
    "Universe of places: 3,206 U.S. cities, towns, villages, boroughs and CDPs.",
    "Facility universe: Google Places API returned ~94,000 self-storage POIs near those 3,206 places. Professional AI Agents LLC then manually filtered false positives (e.g., supermarkets, auto parts), resulting in ~90,000 actual self-storage facilities.",
    "Supply per capita: estimated rentable square feet = facility count × 40,000 (assumed typical facility size). Sqft/person = estimated sqft ÷ population.",
    "Market selection (House requested): California, Idaho, Montana, Washington, Oregon, Arizona, Utah, New Mexico, Wyoming, Colorado. Professional AI Agents LLC selected ~300 lowest-supply (sqft/person) places within these 10 states.",
    "Nationwide additions: To avoid missing standout markets outside the focus states, we added 50 lowest-supply places from the remaining 40 states.",
    "Rent data: Veritec Solutions provided average non-climate, ground-level 10×10 rates for 127 cities. For the remaining 223 cities, the state-level average rate was used.",
    "Final dataset comprises 350 cities encompassing 3,784 facilities as of September 4, 2025. This report is a single-point-in-time snapshot; several key metrics incorporate observations dating back to 2022 and are not continuously updated. It is intended to provide directional market context for identifying potential self-storage opportunities, not to serve as a live database of record."
  ].forEach(b => { wrap(doc, "• " + b, contentW).forEach(ln => { doc.text(ln, margin, y); y += 12; }); });

  y += 6;
  [
    "Assumptions materially affect results. If average facility size differs from 40,000 sqft, or if competitor facility counts/areas are misclassified, supply per capita and “needed facilities” will change. Rows marked as thin data indicate incomplete or inconsistent observations.",
    "Effective rent ($/sf) is estimated as the average advertised 10x10 monthly rate divided by 100. Some markets may have atypical unit mixes, discounts, or fees not reflected in posted rates."
  ].forEach(p => { wrap(doc, p, contentW).forEach(ln => { doc.text(ln, margin, y); y += 12; }); });

  y += 10;
  doc.setDrawColor(...COLORS.stroke); doc.setLineWidth(0.8);
  doc.line(margin, y, pageW - margin, y); y += 14;

  const ownership =
    "Ownership & License. This website and report (including data compilations, analyses, and visualizations) are the proprietary work of Professional AI Agents LLC. They are furnished to House Properties under a license for internal decision support. The materials may not be reproduced, redistributed, publicly posted, or used in any commercial offering, and the license may not be transferred or sublicensed, without prior written consent. Limited sharing with prospective partners or investors under confidentiality is permitted. Third-party data remains the property of its respective owners.";
  wrap(doc, ownership, contentW).forEach(ln => { doc.text(ln, margin, y); y += 12; });

  // ——— Footer (unchanged) ———
  const pages = doc.getNumberOfPages();
  const stampDate = generatedOn;
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    const yFoot = pageH - 18;

    doc.setDrawColor(...COLORS.footStroke); doc.setLineWidth(0.6);
    doc.line(margin, yFoot - 8, pageW - margin, yFoot - 8);

    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...COLORS.footText);
    doc.text(`Page ${p} of ${pages}`, margin, yFoot);
    doc.text(`Confidential • Generated ${stampDate}`, pageW / 2, yFoot, { align: "center" });
    const year = new Date().getFullYear();
    doc.text(`© ${year} Professional AI Agents LLC`, pageW - margin, yFoot, { align: "right" });
  }

  doc.save("MarketScout_Underserved_Report.pdf");
}


  // ---- Layout styles ----
  const section = { marginBottom: 16 };

  return (
    <div style={{ maxWidth: 1150, margin: "0 auto", padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>
        House Properties MarketScout — Filter & Report
      </h1>
      <h1 style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>
        Built by Professional AI Agents LLC
      </h1>
        {/* Notes banner */}
        <div
        style={{
            background: "#f7f7f9",
            border: "1px solid #e5e5ee",
            borderRadius: 8,
            padding: 10,
            marginBottom: 14,
            fontSize: 12.5,
            lineHeight: 1.35,
        }}
        >
        <div>
            <strong>Note:</strong> This dataset contains the{" "}
            <strong>350 towns with the lowest storage supply per capita</strong> —
            the lowest <strong>300</strong> from the target western states
            (CA, ID, MT, WA, OR, AZ, UT, NM, WY, CO) plus the lowest{" "}
            <strong>50</strong> from all other states — out of ~
            <strong>3,206</strong> U.S. towns (population 5,000–25,000).{" "}
            <strong>Facilities</strong> represent the count within ~
            <strong>6.2-mile radius</strong> of each town centroid. Prices are
            currently modeled placeholders.
        </div>
        </div>


      {/* STATES — full-width; 3 rows of pills */}
      <div style={section}>
        <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>States (click to toggle)</label>
        <div
          style={{
            border: "1px solid #ddd",
            borderRadius: 5,
            padding: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridAutoFlow: "column",               // fill by rows, then create new column
              gridTemplateRows: "repeat(3, auto)",  // exactly 3 rows
              gridAutoColumns: "max-content",
              columnGap: 6,
              rowGap: 8,
              alignItems: "start",
              justifyContent: "start",
            }}
          >
            {states.map((s) => {
              const active = selectedStates.includes(s);
              return (
                <button
                  key={s}
                  onClick={() => toggleState(s)}
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: 999,
                    padding: "6px 12px",
                    background: active ? "#111" : "#fff",
                    color: active ? "#fff" : "#111",
                    cursor: "pointer",
                    fontSize: 12,
                    whiteSpace: "nowrap",
                  }}
                  title={s}
                >
                  {s}
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button onClick={selectAllStates}>Select all</button>
            <button onClick={clearStates}>Clear</button>
          </div>
        </div>
      </div>

      {/* CONTROLS — separate grid below states */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
          gap: 12,
          alignItems: "end",
          marginBottom: 16,
        }}
      >
        {/* Search */}
        <div style={{ gridColumn: "span 2", marginBottom: 8  }}>
          <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>Search</label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Town name / key"
            style={{ width: "80%", padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
          />
        </div>

        {/* Population dual slider */}
        <div style={{ gridColumn: "span 3", marginBottom: 8  }}>
          <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>
            Population range (5,000 to 25,000)
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Min: {formatNumber(minPop)}</div>
              <input
                type="range"
                min={5000}
                max={25000}
                step={100}
                value={minPop}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setMinPop(Math.min(v, maxPop));
                }}
                style={{ width: "80%" }}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Max: {formatNumber(maxPop)}</div>
              <input
                type="range"
                min={5000}
                max={25000}
                step={100}
                value={maxPop}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setMaxPop(Math.max(v, minPop));
                }}
                style={{ width: "80%" }}
              />
            </div>
          </div>
        </div>

{/* FACILITY SIZE DEFAULTS (span 2) */}
<div style={{ gridColumn: "span 2", marginBottom: 8 }}>
  <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>
    Facility Size Defaults (Square Feet)
  </label>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      columnGap: 30,
      rowGap: 8,
      alignItems: "end",
    }}
  >
    <div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Small</div>
      <input
        type="number"
        min={1000}
        step={1000}
        value={sizeSmall}
        onChange={(e) => setSizeSmall(Number(e.target.value) || 0)}
        style={{ width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
      />
    </div>

    <div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Medium</div>
      <input
        type="number"
        min={1000}
        step={1000}
        value={sizeMedium}
        onChange={(e) => setSizeMedium(Number(e.target.value) || 0)}
        style={{ width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
      />
    </div>

    <div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>Large</div>
      <input
        type="number"
        min={1000}
        step={1000}
        value={sizeLarge}
        onChange={(e) => setSizeLarge(Number(e.target.value) || 0)}
        style={{ width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
      />
    </div>

    {/* Max Sqft/Cap */}
    <div>
      <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>Max Sqft/Cap</label>
      <input
        value={maxSqftPerCap}
        onChange={(e) => setMaxSqftPerCap(Number(e.target.value) || 0)}
        type="number"
        placeholder="10"
        inputMode="numeric"
        style={{ width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
      />
    </div>
  </div>

  <div style={{ fontSize: 11, color: "#777", marginTop: 6 }}>
    Tip: Adjust these to test “what-if” scenarios. Calculations use counts from your towns.
  </div>
</div>


{/* MIN MEDIAN INCOME (span 3) */}
<div style={{ gridColumn: "span 3", marginBottom: 8, textAlign: "center" }}>
  <label style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
    Median Income Above:
  </label>

  {/* Value display above slider */}
  <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>
    {new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0
    }).format(minIncome)}
  </div>

  {/* Slider */}
  <div style={{ width: "80%", margin: "0 auto" }}>
    <input
      type="range"
      min={60000}
      max={250000}
      step={1000}
      value={minIncome}
      onChange={(e) => setMinIncome(Number(e.target.value) || 60000)}
      style={{ width: "100%" }}
    />
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: 11,
        color: "#888",
        marginTop: 2
      }}
    >
      <span>$60k</span>
      <span>$250k</span>
    </div>
  </div>
</div>



        {/* Thin + Reset + Export */}
        <div style={{ gridColumn: "span 4", display: "flex", gap: 12, paddingLeft: 8, alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={hideThin} onChange={(e) => setHideThin(e.target.checked)} />
            Hide 0 & 1 Facility Towns
          </label>

          <button onClick={resetFilters} style={{ padding: "8px 12px" }}>
            Reset
          </button>

          <div style={{ marginLeft: "auto" }}>
            <button onClick={exportPDF} style={{ padding: "8px 12px" }}>
              Export City List
            </button>
          </div>
        </div>
      </div>

{/* TABLE */}
<div style={{ border: "1px solid #eee", borderRadius: 8, overflow: "hidden" }}>
  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
    <thead style={{ position: "sticky", top: 0, background: "#fff" }}>
      <tr style={{ borderBottom: "1px solid #eee", textAlign: "left" }}>
        {/* New non-sortable Actions column */}
        <th style={{ padding: "10px 12px" }}>Actions</th>

        <th
          style={{ padding: "10px 12px", cursor: "pointer" }}
          onClick={() => handleSort("abbreviated_city")}
          title="Sort by Town"
        >
          Town {sortKey === "abbreviated_city" && (sortDir === "asc" ? "▲" : "▼")}
        </th>

        <th
          style={{ padding: "10px 12px", cursor: "pointer" }}
          onClick={() => handleSort("type")}
          title="Sort by Type"
        >
          Type {sortKey === "type" && (sortDir === "asc" ? "▲" : "▼")}
        </th>

        <th
          style={{ padding: "10px 12px", cursor: "pointer" }}
          onClick={() => handleSort("state")}
          title="Sort by State"
        >
          State {sortKey === "state" && (sortDir === "asc" ? "▲" : "▼")}
        </th>

        <th
          style={{ padding: "10px 12px", cursor: "pointer" }}
          onClick={() => handleSort("population")}
          title="Sort by Population"
        >
          Population {sortKey === "population" && (sortDir === "asc" ? "▲" : "▼")}
        </th>

        <th
          style={{ padding: "10px 12px", cursor: "pointer" }}
          onClick={() => handleSort("median_income")}
          title="Sort by Median Income"
        >
          Median Income {sortKey === "median_income" && (sortDir === "asc" ? "▲" : "▼")}
        </th>

        <th
          style={{ padding: "10px 12px", cursor: "pointer" }}
          onClick={() => handleSort("sqft_per_capita")}
          title="Sort by Sqft/Cap"
        >
          Sqft/Cap {sortKey === "sqft_per_capita" && (sortDir === "asc" ? "▲" : "▼")}
        </th>

        <th
          style={{ padding: "10px 12px", cursor: "pointer" }}
          onClick={() => handleSort("mean_price")}
          title="Sort by Avg 10x10"
        >
          Avg 10x10 {sortKey === "mean_price" && (sortDir === "asc" ? "▲" : "▼")}
        </th>

        <th
          style={{ padding: "10px 12px", cursor: "pointer" }}
          onClick={() => handleSort("num_facilities")}
          title="Sort by Facilities (~6.2mi)"
        >
          Facilities (~6.2mi) {sortKey === "num_facilities" && (sortDir === "asc" ? "▲" : "▼")}
        </th>

        <th
          style={{ padding: "10px 12px", cursor: "pointer" }}
          onClick={() => handleSort("total_rentable_sqft_est")}
          title="Sort by Est. Total Rentable SqFt"
        >
          Est. Total Rentable SqFt {sortKey === "total_rentable_sqft_est" && (sortDir === "asc" ? "▲" : "▼")}
        </th>
      </tr>
    </thead>

    {/* Table body */}
    <tbody>
      {loading ? (
        <tr>
          <td style={{ padding: 16 }} colSpan="10">Loading…</td>
        </tr>
      ) : filtered.length === 0 ? (
        <tr>
          <td style={{ padding: 16 }} colSpan="10">No results with current filters.</td>
        </tr>
      ) : (
        filtered.map((r) => {
          const { city, state, type } = parseTownParts(r.town_name || "");
          const disabled = (r.num_facilities ?? 0) === 0;

          return (
            <tr key={r.town_key} style={{ borderTop: "1px solid #f2f2f2" }}>
              {/* Actions cell with Export button */}
              <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                <button
                  onClick={() => exportFacilitiesPDF(r)}
                  disabled={disabled}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #ddd",
                    background: disabled ? "#f8f9fa" : "#fff",
                    color: disabled ? "#999" : "#111",
                    cursor: disabled ? "not-allowed" : "pointer",
                    fontSize: 12
                  }}
                  title={disabled ? "No facilities to export" : "Export facilities in this town to PDF"}
                >
                  Generate Report
                </button>
              </td>

              <td style={{ padding: "8px 12px", fontWeight: 600 }}>{city}</td>
              <td style={{ padding: "8px 12px" }}>{type || "—"}</td>
              <td style={{ padding: "8px 12px" }}>{state}</td>
              <td style={{ padding: "8px 12px" }}>{formatNumber(r.population)}</td>
              <td style={{ padding: "8px 12px" }}>{formatNumber(r.median_income)}</td>
              <td style={{ padding: "8px 12px" }}>{formatSqftPerCap(r._est_sqft_per_capita ?? r.sqft_per_capita)}</td>
              <td style={{ padding: "8px 12px" }}>
                {formatCurrency(r.mean_price)}{" "}
                <span
                  title={r.price_source === "C" ? "City average" : "State average"}
                  style={{
                    display: "inline-block",
                    padding: "2px 6px",
                    borderRadius: "12px",
                    fontSize: "0.75em",
                    fontWeight: 600,
                    color: r.price_source === "C" ? "#0f5132" : "#084298",
                    backgroundColor: r.price_source === "C" ? "#d1e7dd" : "#cfe2ff",
                    marginLeft: "6px",
                  }}
                >
                  {r.price_source}
                </span>
              </td>
              <td style={{ padding: "8px 12px" }}>{formatNumber(r.num_facilities)}</td>
              <td style={{ padding: "8px 12px" }}>{formatNumber(r._est_total_sqft ?? r.total_rentable_sqft_est)}</td>
            </tr>
          );
        })
      )}
    </tbody>
  </table>
</div>


      <p style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
        Tip: Filter by states and population range, keep Max Sqft/Cap around 10, hide thin data, then export the PDF.
      </p>
    </div>
  );
}